import { useEffect, useMemo, useState } from 'react';
import { components } from '../../api/types';

type DsmEntry = components['schemas']['DsmEntry'];

interface DsmPageProps {
  selectedId: string | null;
  dsmAssociations: DsmEntry[];
  measureNames: string[];
  takenMeasureMask: boolean[];
  loading: boolean;
}

const DsmPage = ({ selectedId, dsmAssociations, measureNames, takenMeasureMask, loading }: DsmPageProps): JSX.Element => {
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<string>('all');
  
  // const lastLoadedId = useRef<string | null>(null);

  // useEffect(() => {
  //   let cancelled = false;
  //   const fetchData = async () => {
  //     setLocalLoading(true);
  //     try {
  //       const [dsmRes, takenRes] = await Promise.all([
  //         getDsmAssociations(),
  //         getTakenMeasures(),
  //       ]);
  //       if (!cancelled) {
  //         setClientData(dsmRes);
  //         setTakenMeasures(new Set(takenRes.map(m => m.measureName)));
  //         lastLoadedId.current = selectedId;
  //       }
  //     } finally {
  //       if (!cancelled) setLocalLoading(false);
  //     }
  //   };
  //   fetchData();
  //   return () => {
  //     cancelled = true;
  //   };
  // }, [selectedId]);

  /* Check URL for diagnosis parameter */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const diagnosisParam = urlParams.get('diagnosis');
    if (diagnosisParam) {
      setSelectedDiagnosis(diagnosisParam);
    }
  }, []);

  const grouped = useMemo(() => {
    type MeasureAgg = {
      descriptions: Set<string>;
      criteria: Set<string>;
      specify: Set<string>;
      taken?: boolean;
    };
    const byDiagnosis = new Map<string, Map<string, MeasureAgg>>();

    for (const e of dsmAssociations) {
      const diagnosis = (e as any).diagnosis as string;
      const measure = (e as any).measure as string;
      const description = (e as any).description as string | undefined;
      const dsmCriteria = (e as any).dsmCriteria ?? (e as any).dsm_criteria ?? '';
      const dsmSpecify = (e as any).dsmSpecify ?? (e as any).dsm_specify ?? '';

      if (!diagnosis || !measure) continue;

      if (!byDiagnosis.has(diagnosis)) byDiagnosis.set(diagnosis, new Map());
      const byMeasure = byDiagnosis.get(diagnosis)!;

      if (!byMeasure.has(measure)) {
        byMeasure.set(measure, {
          descriptions: new Set<string>(),
          criteria: new Set<string>(),
          specify: new Set<string>(),
          taken: takenMeasureMask[measureNames.indexOf(measure)],
        });
      }
      const agg = byMeasure.get(measure)!;
      if (description) agg.descriptions.add(description);
      if (dsmCriteria) agg.criteria.add(String(dsmCriteria));
      if (dsmSpecify) agg.specify.add(String(dsmSpecify));
      // if (takenMeasureMask[measureNames.indexOf(measure)]) agg.taken = true;
    }

    return byDiagnosis;
  }, [dsmAssociations, takenMeasureMask, measureNames]);

  // Filter diagnoses based on selection
  const filteredDiagnoses = useMemo(() => {
    const entries = Array.from(grouped.entries());
    if (selectedDiagnosis === 'all') {
      return entries;
    }
    return entries.filter(([diagnosis]) => diagnosis === selectedDiagnosis);
  }, [grouped, selectedDiagnosis]);

  const handleDiagnosisChange = (diagnosis: string) => {
    setSelectedDiagnosis(diagnosis);
    // Update URL without page reload
    const url = new URL(window.location.href);
    if (diagnosis === 'all') {
      url.searchParams.delete('diagnosis');
    } else {
      url.searchParams.set('diagnosis', diagnosis);
    }
    window.history.pushState({}, '', url.toString());
  };

  const copyLink = (diagnosis: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('diagnosis', diagnosis);
    navigator.clipboard.writeText(url.toString());
    // You could add a toast notification here
  };

  const style = `
    :root {
      --card-bg: #ffffff;
      --muted-bg: #f8fafc;
      --border: #e5e7eb;
      --border-strong: #cbd5e1;
      --text: #0f172a;
      --subtext:rgb(109, 115, 125);
      --chip-bg: #eef2ff;
      --chip-text:rgb(0, 0, 0);
      --accent: #4f46e5;
    }

    .page { max-height: 80vh; overflow-y: auto; }

    /* Filter controls */
    .filter-container {
      margin-bottom: 1.5rem;
      display: flex;
      align-items: center;
      padding-left: 1rem;
      gap: 1rem;
      flex-wrap: wrap;
    }
    .filter-label {
      color: #eef2f8;
      font-weight: 600;
      font-size: 1rem;
    }
    .filter-select {
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--border-strong);
      border-radius: 6px;
      background: var(--card-bg);
      color: var(--text);
      font-size: 1rem;
      min-width: 200px;
    }
    .filter-select:focus {
      outline: none;
      border-color: var(--accent);
      box-shadow: 0 0 0 2px rgba(79,70,229,0.1);
    }

    /* Diagnosis card */
    .dsm-diagnosis-card {
      background: var(--muted-bg);
      border: 1px solid var(--border);
      border-radius: 6px;
      padding: 1.1rem 1.2rem 1rem;
      margin-bottom: 1.25rem;
    }
    .dsm-diagnosis {
      margin: 0 0 .75rem 0;
      color: var(--text);
      font-weight: 700;
      font-size: 1.15rem;
      letter-spacing: .2px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .copy-link-btn {
      background: var(--accent);
      color: white;
      border: none;
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
      font-size: 0.8rem;
      cursor: pointer;
      transition: background-color 0.2s;
    }
    .copy-link-btn:hover {
      background: #3730a3;
    }

    /* Measure grid inside diagnosis */
    .measure-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: .8rem;
    }
    @media (min-width: 1050px) {
      .measure-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (min-width: 1400px) {
      .measure-grid { grid-template-columns: repeat(3, 1fr); }
    }

    /* Measure card */
    .measure-card {
      background: var(--card-bg);
      border: 1px solid var(--border);
      border-radius: 6px;
      min-width: 320px;
      padding: .85rem .95rem;
      box-shadow: 0 1px 6px rgba(0,0,0,0.04);
      transition: box-shadow .15s ease, transform .15s ease;
    }
    .measure-card:hover {
      box-shadow: 0 6px 18px rgba(0,0,0,0.07);
      transform: translateY(-1px);
    }

    /* Measure header */
    .measure-title {
      margin: 0 0 .55rem 0;
      font-weight: 600;
      color: var(--text);
      font-size: 1.1rem;
      line-height: 1.25rem;
    }
    .measure-rule {
      border: none;
      border-top: 1px solid var(--border);
      margin: .5rem 0 .7rem 0;
    }

    /* Label + content rows */
    .row {
      display: grid;
      grid-template-columns: 120px 1fr;
      align-items: start;
      gap: .6rem;
      margin-bottom: .5rem;
    }
    .label {
      color: var(--subtext);
      font-weight: 600;
      font-size: 1.1rem;
      line-height: 1.3rem;
      min-width: 0;
    }
    .content {
      color: var(--text);
      font-size: 1.1rem;
      line-height: 1.4rem;
      min-width: 0;
    }

    /* Chips for multiple values */
    .chips { display: flex; flex-wrap: wrap; gap: .35rem .4rem; }
    .chip {
      background: var(--chip-bg);
      color: var(--chip-text);
      border: 1px solid var(--border-strong);
      padding: .25rem .75rem;
      border-radius: 15px;
      font-size: 1.0rem;
      line-height: 1.1rem;
    }

    /* Status pills */
    .status-pill { display:inline-block; padding:.2rem .6rem; border-radius:12px; font-size:.85rem; font-weight:600; border:1px solid; }
    .status-taken { background:#d1fae5; color:#065f46; border-color:#10b981; }
    .status-not-taken { background:#fef2f2; color:#991b1b; border-color:#ef4444; }

    /* Header pills (optional accent) */
    .pill {
      display: inline-block;
      background: rgba(79,70,229,0.08);
      color: var(--accent);
      border: 1px solid rgba(79,70,229,0.2);
      padding: .15rem .5rem;
      border-radius: 999px;
      font-size: 1.1rem;
      margin-left: .75rem;
      vertical-align: middle;
    }
  `;

  const toArray = (s: Set<string>) => Array.from(s).filter(Boolean);

  /* Loading screen */
  if (loading) {
    return (
      <>
        <style>{style}</style>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1rem' }}>
          <h1 className="text">DSM-5 Associations</h1>
          <span style={{ fontSize: '1.2rem', color: '#CCC' }}>
            DSM-5 diagnostic criteria and specifiers mapped to assessment measures
          </span>
        </div>
        <div className="filter-container">
          <p style={{ padding: "0 5%", fontSize: '15pt', color: '#eef2f8'}}>Loading...</p>
        </div>
      </>
    );
  }

  /* Main content */
  return (
    <>
      <style>{style}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '1rem', marginBottom: '1rem' }}>
        <h1 className="text">DSM-5 Associations</h1>
        <span style={{ fontSize: '1.2rem', color: '#CCC' }}>
          DSM-5 diagnostic criteria and specifiers mapped to assessment measures
        </span>
      </div>
      
      <div className="filter-container">
        <label className="filter-label">Filter by diagnosis:</label>
        <select 
          className="filter-select"
          value={selectedDiagnosis}
          onChange={(e) => handleDiagnosisChange(e.target.value)}
        >
          <option value="all">All Diagnoses</option>
          {Array.from(grouped.keys()).sort().map(diagnosis => (
            <option key={diagnosis} value={diagnosis}>
              {diagnosis}
            </option>
          ))}
        </select>
      </div>

      {filteredDiagnoses.map(([diagnosis, byMeasure]) => (
        <div key={diagnosis} className="dsm-diagnosis-card">
          <h4 className="dsm-diagnosis">
            <span>{diagnosis}</span>
            <button 
              className="copy-link-btn"
              onClick={() => copyLink(diagnosis)}
              title="Copy link to this diagnosis"
            >
              Copy Link
            </button>
          </h4>

          <div className="measure-grid">
            {Array.from(byMeasure.entries()).map(([measure, agg]) => (
              <div key={`${diagnosis}::${measure}`} className="measure-card">
                <div className="measure-title">{measure}
                  {typeof agg.taken === 'boolean' && (
                    <span style={{ marginLeft: '.5rem' }} className={`status-pill ${agg.taken ? 'status-taken' : 'status-not-taken'}`}>
                      {agg.taken ? 'Taken' : 'Not Taken'}
                    </span>
                  )}
                </div>
                <hr className="measure-rule" />

                {/* Assesses */}
                <div className="row">
                  <div className="label">Assesses</div>
                  <div className="content">
                    {toArray(agg.descriptions).length ? (
                      <div className="chips">
                        {toArray(agg.descriptions).map((d, i) => (
                          <span key={`d-${i}`} className="chip">{d}</span>
                        ))}
                      </div>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>

                {/* DSM-5 Criteria */}
                <div className="row">
                  <div className="label">DSM-5 Criteria</div>
                  <div className="content">
                    {toArray(agg.criteria).length ? (
                      <div className="chips">
                        {toArray(agg.criteria).map((c, i) => (
                          <span key={`c-${i}`} className="chip">{c}</span>
                        ))}
                      </div>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>

                {/* Specifier */}
                <div className="row">
                  <div className="label">Specifier</div>
                  <div className="content">
                    {toArray(agg.specify).length ? (
                      <div className="chips">
                        {toArray(agg.specify).map((s, i) => (
                          <span key={`s-${i}`} className="chip">{s}</span>
                        ))}
                      </div>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {/* DSM-5 Citation */}
      <div style={{ 
        marginTop: '2rem', 
        paddingTop: '1rem', 
        borderTop: '1px solid var(--border)', 
        fontSize: '1.2rem', 
        color: 'var(--subtext)', 
        textAlign: 'center' 
      }}>
        Diagnostic criteria and specifiers are adapted from the Diagnostic and Statistical Manual of Mental Disorders, Fifth Edition (DSM-5®). 
        Copyright 2013 American Psychiatric Association. All rights reserved.
      </div>
    </>
  );
};

export default DsmPage;
